// Terminology policy (§9.5): Traefik domain nouns — router, service, middleware, entrypoint,
// resolver, snapshot, group, certResolver — stay in ENGLISH in both locales. Half-translated
// infrastructure vocabulary is worse than useless for an audience that reads the Traefik docs.
//
// The same rule is applied to "static config": Traefik's own documentation splits its
// configuration into *static* and *dynamic*, and those two words are how an operator searches for
// the thing this panel is editing.
export default {
    general: {
        add:     'Add',
        edit:    'Edit',
        cancel:  'Cancel',
        save:    'Save',
        delete:  'Delete',
        close:   'Close',
        confirm: 'Confirm',
        back:    'Back',
        search:  'Search',
        yes:     'Yes',
        no:      'No',
        actions: 'Actions',
        replace: 'Replace',
        loading: 'Loading…',
        no_data: 'No data',
        email:   'E-mail',
        created: 'Created',
        updated: 'Updated',
        none:    'None',
        // The label beside a client-side pager. Shared rather than per-screen (W12.5): the runtime
        // tables and the group list draw the same fifty-row pager and must not say it differently.
        page_range: '{from}–{to} of {total}',
    },
    // Read by the house shell (vue-spec templates/shell): the sidebar's brand row.
    brand: {
        product_name: 'Traefik Proxy Manager',
        tagline:      'Fleet console',
    },
    nav: {
        dashboard:           'Dashboard',
        issues:              'Issues',
        instances:           'Instances',
        clients:             'Clients',
        registry:            'Registry',
        users:               'Users',
        profile:             'Profile',
        navigation:          'Navigation',
        language:            'Language',
        search_hint:         'Jump to an instance, a client or a screen.',
        sign_out:            'Sign out',
        // D78 — local to TPM only: every one of the caller's Traefik Proxy Manager sessions, on
        // every device. It never touches the IT Team ID sign-in or another application, and the
        // label names that scope so it can never read as a global sign-out (handoff §7.1).
        sign_out_all_tpm:    'Sign out of all TPM sessions',
        // The sidebar's group headers (`layouts/layout.ts` → NAV_GROUPS).
        group_fleet:         'Fleet',
        group_system:        'System',
    },
    // The mobile drawer's two affordances (templates/shell).
    layout: {
        open_menu:  'Open menu',
        close_menu: 'Close menu',
    },
    // The two-state colour-mode toggle in the topbar (theme.md): the tooltip names the mode a
    // click switches TO. Light and Dark only — there is no System option.
    color_mode: {
        switch_to_light: 'Switch to light mode',
        switch_to_dark:  'Switch to dark mode',
    },
    auth: {
        sign_in:        'Sign in',
        sign_in_with:   'Sign in with IT Team ID',
        sign_in_hint:   'You will be redirected to your company identity provider.',
        signing_in:     'Signing you in…',
        signed_in_as:   'Signed in as {email}',
        account:        'Account',
        no_access_title: 'No access yet',
        // §10.2 — the exact copy for a provisioned user with no role and no assignment.
        no_access:      'Your account was created. Ask an administrator to grant you access.',
        check_again:    'Check again',

        // F36 — the sign-in callback's failure contract: a failed callback 302s to
        // /login?reason=<code>, and these five explain each code. The set mirrors the
        // loginReason* constants in tpm-api's HTTP_API/Authentication.go; an unknown code
        // renders sign_in_failed, and the raw query value is never shown.
        login_failed: {
            title:          'Sign-in failed',
            idp_refused:    'The identity provider refused the sign-in — your account does not have access to this portal. Contact your administrator.',
            state_mismatch: 'The sign-in attempt expired or did not match. Start again.',
            incomplete:     'The sign-in did not complete. Start again.',
            not_allowed:    'Your identity was verified, but this account does not have access to this portal. Contact your administrator.',
            sign_in_failed: 'Sign-in failed. Try again; if it keeps happening, contact your administrator.',
        },

        // D78 — the signed-out page is /login?signed_out=<state>, and these explain the state.
        // Both confirmed sign-outs are local to Traefik Proxy Manager: the IT Team ID sign-in is
        // untouched, which is why Sign in may not ask for a password. `unconfirmed` means the
        // cookie is gone but the server could not confirm the session record was ended.
        signed_out_title:       'Signed out',
        signed_out_device:      'You are signed out of Traefik Proxy Manager on this device. Your IT Team ID sign-in is not affected, so Sign in may not ask for a password.',
        signed_out_all:         'You are signed out of all your Traefik Proxy Manager sessions on every device. Your IT Team ID sign-in is not affected.',
        signed_out_unconfirmed: 'Your session cookie was removed, but the server could not confirm the session was ended. Sign in and use “Sign out of all TPM sessions”, or try again later.',
    },
    // §7.1's landing view. Every tile links into the list its number was counted over, so the two
    // can never disagree; `libs/dashboard.ts` argues which of them are filtered and why the others
    // are not.
    dashboard: {
        fleet:            'Fleet',
        needs_attention:  'Needs attention',
        all_clear:        'Nothing needs attention',
        all_clear_hint:   'No open issues in the instances you can see.',
        view_all:         'All issues',
        more_issues:      'Showing {shown} of {total} — see all',
        panel_version:    'Panel v{version}',
        refresh_failed:   'Could not refresh',

        tiles: {
            instances:       'Instances',
            offline:         'Offline',
            offline_hint:    'Opens the offline sites',
            open_issues:     'Open issues',
            critical_issues: 'Critical',
            clients:         'Clients',
            groups:          'Groups',
            groups_hint:     'Groups live inside an instance',
        },

        split: {
            healthy:      'Healthy',
            degraded:     'Degraded',
            offline:      'Offline',
            pending:      'Pending',
            // §9.5's rule, stated where the number is: a site that has never reported is not down.
            pending_hint: 'Pending means never reported — created, and not installed yet. It is not offline.',
        },
    },
    // §7.1's issues, §9.5's renderer. The sixteen `issues.<type>` entries below are message
    // templates: the server sends `type` + `params{}` and the SPA renders `t('issues.' + type,
    // params)`, so **every** placeholder here names a param `Logic/IssueRules.go` actually sends.
    // Adding a seventeenth type is two entries here and two in sl.ts — `yarn check:i18n` fails the
    // moment either half is missing.
    issues: {
        title:          'Issues',
        issue:          'Issue',
        severity_label: 'Severity',
        since_label:    'Open for',
        since:          'for {ago}',
        open:           'Fix',
        open_hint:      'Opens the tab that fixes this',
        critical_open:  'Critical: {count}',

        severity: {
            critical: 'Critical',
            warning:  'Warning',
        },

        // The short name of each type — the filter's options and nothing else. Traefik nouns stay
        // English here as everywhere (see the file header).
        types: {
            host_cpu:                    'Host CPU',
            host_memory:                 'Host memory',
            host_disk:                   'Host disk',
            cert_expiring:               'Certificate expiring',
            cert_expired:                'Certificate expired',
            instance_offline:            'Instance offline',
            stale_applied_version:       'Stale applied version',
            tag_mismatch:                'Image tag mismatch',
            outdated_agent:              'Outdated agent',
            reload_failure:              'Reload failure',
            router_service_errors:       'Router / service errors',
            clock_drift:                 'Clock drift',
            config_pending_files:        'Pending certificate files',
            static_config_drift:         'Static config drift',
            agent_outbox_overflow:       'Agent outbox overflow',
            flatten_invariant_violation: 'Flatten invariant violation',
        },

        // The sixteen messages. Durations, byte counts, percentages and day counts arrive as raw
        // numbers and are formatted by `libs/issue.ts` before substitution — `{threshold_seconds}`
        // is already "3 h 20 min" by the time it reaches this sentence.
        //
        // **No message interpolates an elapsed time the server computed**, and none can: the server
        // sends the *anchor* (`{last_seen_at}`, `{delivered_at}`, `{mismatched_at}`, `{outdated_at}`,
        // `{over_threshold_at}`) because a value that grows every minute would rewrite the served
        // document and cost `/dashboard` its 304 on every poll (Logic/IssueRules.go states the
        // contract). How long ago that instant was is `{open_for}`, which `libs/issue.ts` computes
        // here from the issue's own `since`.
        host_cpu:              'CPU at {cpu_percent}, above the {threshold_percent} threshold since {over_threshold_at} ({open_for}).',
        host_memory:           'Memory at {used_percent}, above the {threshold_percent} threshold — {memory_available_bytes} available of {memory_total_bytes}.',
        host_disk:             'Filesystem {path} is {used_percent} full, above the {threshold_percent} threshold — {available_bytes} free of {total_bytes}.',
        cert_expiring:         'Certificate for {domains} expires in {days_left} ({not_after}); the threshold is {threshold_days}.',
        cert_expired:          'Certificate for {domains} expired on {not_after}. Every browser reaching that hostname now gets an error page.',
        instance_offline:      'No sync since {last_seen_at} ({open_for}), past the {threshold_seconds} threshold. Traefik keeps serving its cached configuration.',
        stale_applied_version: 'Version {delivered_version} was delivered at {delivered_at} ({open_for}) and the site is still serving {applied_version} (threshold {threshold_seconds}).',
        tag_mismatch:          'Traefik is running {running_version} while the assigned image tag is {desired_tag} — since {mismatched_at} ({open_for}), past the {threshold_seconds} threshold.',
        outdated_agent:        'Agent {agent_version} does not match the expected {expected_version} — since {outdated_at} ({open_for}), past the {threshold_seconds} threshold.',
        reload_failure:        'Traefik refused version {delivered_version}; the site is still serving {applied_version}.',
        router_service_errors: 'Traefik reports objects in error — routers: {router_error_count}, services: {service_error_count}, middlewares: {middleware_error_count} ({objects}).',
        clock_drift:           'The site\'s clock differs from the panel\'s by {drift_seconds}, past the {threshold_seconds} threshold. Every time this host reports is off by that much.',
        config_pending_files:  'The site is holding a published snapshot back — certificate files it requires have not landed on disk (missing: {pending_count}; {pending_files}).',
        static_config_drift:   'The traefik.yml on the host is not the one the panel last shipped — on disk {on_disk_sha256}, rendered {rendered_sha256}. Applying static configuration is refused until this is resolved.',
        agent_outbox_overflow: 'The site discarded telemetry while the panel was unreachable (envelopes dropped: {dropped_count}; last sync {last_seen_at}). There is a gap in this instance\'s data.',
        flatten_invariant_violation: 'A publish aborted: two groups claim the same definition key. The instance is still serving its last good snapshot, and no operator edit will clear this.',
    },
    client: {
        clients:    'Clients',
        client:     'Client',
        add:        'Add client',
        edit:       'Edit client',
        delete:     'Delete client',
        name:       'Name',
        instances:  'Instances of this client',
        acme_email: 'ACME e-mail',
        // D37 — the address is copied onto an instance at creation; it is a default, not a link.
        acme_email_hint:        'Used as the Let\'s Encrypt contact address. New instances of this client start from it.',
        acme_email_edit_notice: 'Changing this address does not change existing instances. It only changes the default that the next instance of this client starts from.',
        delete_confirm:         'Delete client “{name}”? This can only be done while it has no instances.',
        delete_blocked:         'Client “{name}” still has instances. Delete them first.',
    },
    instance: {
        instances:     'Instances',
        instance:      'Instance',
        add:           'Add instance',
        delete:        'Delete instance',
        name:          'Name',
        client:        'Client',
        select_client: 'Select a client',
        image_tag:     'Traefik version',
        acme_email:    'ACME e-mail',

        name_hint:    'Letters, digits, dot, dash and underscore; up to 64 characters. Used in the install script and the site\'s compose file.',
        name_invalid: 'Use only letters, digits, “.”, “-” and “_”, up to 64 characters.',

        image_tag_hint:          'A pinned version, for example {example}. Floating tags such as “latest” cannot be checked against the supported range.',
        image_tag_settings_hint: 'A pinned version, for example {example}. Changing it recreates the container at the site; the configuration file is not rewritten.',
        image_tag_invalid:       'Enter a pinned version such as {example}.',

        acme_email_create_hint:    'Prefilled from the selected client. Change it if this instance uses a different contact address.',
        // §7.10 rule 1 / §6.1 — the one static-config-bearing field on the settings form.
        acme_email_static_notice:  'Saving this field ships a new static configuration to the site.',
        static_config_queued:      'A static configuration change was queued for delivery to the site.',

        // The image tag is the one edit that changes no file and replaces the container (§2.2,
        // §8.4). It is confirmed before it is sent, because the operator is restarting somebody
        // else's proxy.
        image_tag_confirm_title:    'Recreate the container',
        image_tag_confirm:          'Change the Traefik version from {from} to {to}? The container at the site is replaced with one running the new image, keeping its name, labels and networks. Routing is interrupted while it comes back up, and the agent rolls back automatically if the new container does not come up healthy.',
        recreate_queued:            'The container will be recreated on {tag}.',
        static_and_recreate_queued: 'A static configuration change and a container recreate were queued for the site.',
        view_static_config:         'Open static config',

        update_channel: 'Update channel',
        // D22 — the channel is blast-radius control, so the description says what choosing Canary
        // actually costs the operator who chose it rather than describing the word.
        update_channel_hint: 'Canary sites take a new agent release first, so a bad one is found on a handful of sites instead of on all of them. Everything else stays on Stable.',
        channel: {
            stable: 'Stable',
            canary: 'Canary',
        },

        // §13.2's lockstep, as one site's reported agent version reads against it (W7.7). The
        // comparison is against the version the panel *expects* — a rollback pin moves it away
        // from the panel's own — which is why the tooltip names that version rather than "panel".
        agent: {
            current:  'Up to date',
            outdated: 'Outdated',
            ahead:    'Newer than expected',
            unknown:  'Not reported yet',
            expected: 'Expected agent version: {version}',
        },

        // §6.2's `self_update.enabled`, which is the AND of two switches: this instance's, and the
        // deployment's `AGENT_SELF_UPDATE_ENABLED` (§13.1). The vocabulary keeps them apart,
        // because "paused" and "paused fleet-wide" are two different tickets.
        self_update: {
            label:        'Self-update',
            field:        'Self-update for this instance',
            hint:         'When on, the agent at this site replaces itself with the version this panel expects — it pulls the image, tests it before using it, and rolls back automatically if the new one does not come up. Turn it off to hold this one site back; it keeps serving either way.',
            on:           'Self-update on',
            paused:       'Self-update off',
            paused_fleet: 'Paused fleet-wide',
        },

        update: {
            column: 'Updates',

            outdated_title: 'Outdated agent',
            outdated_hint:  'This site reports agent {reported}; the expected version is {expected}.',

            ahead_title: 'Agent ahead of the fleet',
            ahead_hint:  'This site reports agent {reported} and the fleet is pinned to {expected}. The agent moves itself back down to the pinned version.',

            paused_title: 'Self-update paused',

            converging_hint:      'Self-update is on: the agent replaces itself within 30 minutes of a release, spread across the fleet so that a bad release is not taken by every site at once.',
            paused_instance_hint: 'Self-update is off for this instance, so it stays on its current version until somebody turns it back on under Settings.',
            paused_fleet_hint:    'Self-update is paused for the whole fleet. It is set where the panel is deployed (AGENT_SELF_UPDATE_ENABLED) and cannot be changed from here.',
            paused_both_hint:     'Self-update is off for this instance and paused for the whole fleet. Turning this instance back on changes nothing until the fleet-wide switch is on again.',

            // §8.5 — the answer to "did the panel restart my agent?", on the screen where that
            // question is asked. It did not: nothing queues a self-update.
            queue_note: 'A self-update is started by the agent itself and never appears in this queue.',

            fleet_paused_title: 'Self-update paused fleet-wide',
            fleet_paused_hint:  'No agent updates itself while this is off, whatever each instance is set to. It is set where the panel is deployed (AGENT_SELF_UPDATE_ENABLED) and cannot be changed from the panel.',
        },

        enrolment: {
            label:    'Enrolment',
            pending:  'Not installed yet',
            enrolled: 'Enrolled',
        },

        // Presence (§4.4) — a third axis, distinct from enrolment (history) and from status
        // (health). "Not reporting yet" is not "Offline": nothing has been installed at that
        // site, and calling it down sends an operator to debug a path that does not exist.
        presence: {
            label:      'Agent',
            online:     'Online',
            offline:    'Offline',
            never_seen: 'Not reporting yet',
        },
        last_seen: 'last report {at}',

        // §9.5's status colours. Fed by GET /instances/:id/state from M5 onwards.
        status: {
            healthy:       'Healthy',
            degraded:      'Degraded',
            config_stale:  'Config stale',
            reload_failed: 'Reload failed',
            offline:       'Offline',
            pending:       'Pending',
        },

        tabs: {
            overview:      'Overview',
            runtime:       'Runtime',
            groups:        'Groups',
            snapshots:     'Snapshots',
            commands:      'Commands',
            certificates:  'Certificates',
            static_config: 'Static config',
            access:        'Access',
            settings:      'Settings',
            install:       'Install',
        },
        tab_pending: 'This screen arrives in {milestone}.',

        read_only:      'Read-only',
        read_only_hint: 'You have read access to this instance. Changes are not available to you.',

        not_available:      'Instance not available',
        not_available_hint: 'It does not exist, or you do not have access to it.',

        snapshot_version:      'Published snapshot',
        static_configuration:  'Static configuration',
        entrypoints:           'Entrypoints',
        resolvers:             'Resolvers',
        redirect:              'HTTP → HTTPS redirect',
        redirect_permanent:    'Enabled, permanent (301).',
        redirect_temporary:    'Enabled, temporary (302).',
        redirect_disabled:     'Disabled.',

        danger_zone: 'Danger zone',
        delete_hint: 'Removes the instance from the panel and revokes its agent credentials.',
        // D11 — the consequence has to be read before the decision, not discovered after it.
        delete_confirm: 'Delete instance “{name}”? The Traefik container at the site keeps running and keeps serving its current configuration, but it will stop receiving changes and cannot be reconnected.',
    },
    // The install tab (§9.1, W3.9). Two subjects: a credential an operator handles by hand, and
    // the four-step report of what a customer's host has done with it (§6 step 2, §13.3).
    install: {
        command_title: 'Install command',
        intro:         'Run this on the customer\'s host, as root. It installs Docker if it is missing, creates /opt/traefik, starts Traefik together with the agent, and enrolls this instance.',

        none_yet_title: 'No install command yet',
        none_yet:       'Generate one when you are ready to install. It can be used once and is valid for a limited time.',
        generate:       'Generate install command',
        regenerate:     'Regenerate',
        // §6.1 — minting invalidates every unused predecessor, which is what makes regenerating
        // a real mitigation for a leaked link rather than a second live credential beside it.
        regenerate_confirm: 'Generate a new install command? The previous link stops working immediately, including one already pasted into a terminal.',

        copy:             'Copy',
        copied:           'Install command copied',
        copy_unsupported: 'The browser did not allow the copy. Select the command and copy it manually.',
        mint_failed:      'The install command could not be generated',

        // R25 — the blast radius, in the words an operator can act on.
        secret_title: 'Treat this command as a password',
        secret_hint:  'The link contains a one-time token. Anyone who has it can enroll an agent for this instance and receive its configuration and its certificates. It is shown once and the panel keeps no copy of it — leave this page and you will have to generate a new one.',
        run_as_root:  'The script needs root on Linux (x86_64 or aarch64). If port 80 or 443 is already in use it stops and changes nothing.',

        expires_in:   'Valid for {value}',
        expired:      'Expired',
        expired_hint: 'This link can no longer be used. Generate a new one to install.',
        remaining_hm: '{hours} h {minutes} min',
        remaining_ms: '{minutes} min {seconds} s',
        remaining_s:  '{seconds} s',

        // §9.6 — the page's secret-bearing half requires write access; the endpoint enforces it.
        write_required: 'Generating an install command requires write access to this instance.',

        progress_title: 'Installation progress',
        poll_hint:      'Checking every few seconds. The agent reports every 10 seconds, and a site that has been silent for {seconds} seconds counts as offline.',
        online_title:   'The site is online',
        online_hint:    'The agent is reporting and Traefik is being served its configuration. Changes made in the panel now reach this site on their own.',

        steps: {
            created: {
                title: 'Instance created',
                done:  'Created {at}',
            },
            script_fetched: {
                title: 'Install script fetched',
                // §13.3 — the source IP is recorded so an unexpected fetch is visible.
                done:         'Fetched {at} from {ip}',
                done_no_ip:   'Fetched {at}',
                done_unknown: 'The host has run the install script.',
                pending:      'Waiting for the host to fetch the script.',
            },
            enrolled: {
                title:        'Agent enrolled',
                done:         'Enrolled {at}',
                done_unknown: 'The agent has enrolled.',
                pending:      'Waiting for the agent to exchange its one-time token.',
            },
            online: {
                title:   'Reporting',
                done:    'Last report {at}',
                stale:   'Nothing since {at}.',
                pending: 'Waiting for the first report.',
            },
        },
    },
    // The live-state screens (§9.1's overview and runtime tabs, W5.6). Two rules run through this
    // whole block: the Traefik nouns stay English in both locales (§9.5), and nothing here ever
    // paraphrases third-party output — Traefik's reload errors and Docker's own words are rendered
    // verbatim in a `<pre>`, so there is no key for them.
    runtime: {
        poll_failed:  'Refresh failed — showing the last report',
        collected_at: 'collected {at}',

        // ADR-0007's three facts. They are three labels because they are three questions with
        // three different answers and three different owners.
        delivered: 'Delivered',
        applied:   'Applied',
        health:    'Health',

        health_ok:       'No errors',
        health_degraded: 'Not fully healthy',
        health_errors:   '{count} with errors',
        health_unknown:  'Not reported yet',
        unreachable:     'Traefik unreachable',
        open_runtime:    'Show them',

        unreachable_title: 'The agent cannot reach Traefik',
        unreachable_hint:  'The agent is reporting and the Traefik API beside it is not answering. The router, service and middleware lists below are empty because nothing could be read — not because Traefik has none.',

        clock_drift_title: 'This site\'s clock is out of step',
        clock_drift_hint:  'The agent\'s clock differs from the panel\'s by about {seconds} s. The panel\'s time is used for every timestamp on this page, so nothing here is wrong — but certificate renewals and log correlation at the site will be.',

        dropped_telemetry: 'The panel was unreachable long enough for the agent to discard {count} telemetry report(s). The history has a gap; nothing critical was lost.',

        entrypoints_live:     'Traefik is listening on',
        entrypoint_redirects: 'Redirects to {to}',

        // One sentence per rung of §9.5's ladder, naming the fact the rung was chosen from.
        hint: {
            pending:       'Nothing has reported for this instance yet. Run the install command on the customer\'s host.',
            offline:       'No report for more than {seconds} seconds. Traefik at the site keeps serving its cached configuration.',
            reload_failed: 'Traefik did not confirm the reload for v{version}. It is still serving the previous configuration.',
            stalled:       'Traefik never fetched v{version}. It is either down or pointed at a different configuration source.',
            config_stale:  'v{delivered} was published; the site is still running v{applied}.',
            unreachable:   'The agent is reporting, but the Traefik API beside it is not answering.',
            pending_files: 'Waiting for {count} certificate file(s) the published configuration needs.',
            degraded:      'Applied, with {count} router, service or middleware in error.',
            healthy:       'Serving v{version} with no reported errors.',
        },

        // §8.6's applyWatch verdicts (ADR-0004 decision 6). "No reload needed" is a success: it is
        // reachable only on positive evidence, never as a fall-through.
        apply: {
            applied:     'Reloaded',
            noop:        'No reload needed',
            failed:      'Rejected',
            stalled:     'Never fetched',
            unconfirmed: 'Unconfirmed',
            unknown:     'Not decided yet',

            reason_unknown: 'The agent did not report which branch decided this.',
            no_detail:      'Traefik reported nothing further.',

            reason: {
                byte_identical:  'The document served was byte-identical to the one already applied, so there was nothing to reload.',
                reload_observed: 'Traefik\'s reload counters advanced past the baseline taken before it fetched this version.',
                equivalent:      'Traefik fetched a different document and declined to reload it, with no error in its log — it considered the two equivalent.',
                rejected:        'Traefik logged a provider error for this document and kept the previous one.',
                unconfirmed:     'No reload was observed and the Traefik container log could not be read, so the outcome is unknown.',
                not_fetched:     'Traefik never fetched this version within the watch window.',
            },

            title: {
                apply_failed:      'Traefik rejected this configuration',
                apply_stalled:     'Traefik is not fetching its configuration',
                apply_unconfirmed: 'The reload could not be confirmed',
            },
            hint: {
                apply_failed:      'v{version} was published and refused. The site keeps serving the last configuration that loaded, so nothing broke — but the change is not live. Traefik\'s own error follows.',
                apply_stalled:     'v{version} was published and Traefik never asked for it. Check that the container is running and that its static configuration still points at the agent.',
                apply_unconfirmed: 'No reload was observed for v{version} and the Traefik container log could not be read to find out why. The panel reports this as unconfirmed rather than guessing.',
            },
        },

        // Traefik's own words for a runtime object's state, kept as Traefik spells them.
        status: {
            enabled:  'Enabled',
            warning:  'Warning',
            disabled: 'Disabled',
            error:    'Error',
            unknown:  'Unknown',
        },

        host: {
            title:  'Host',
            cpu:    'CPU',
            memory: 'Memory',
            swap:   'Swap',
            load:   'Load average',
            cores:  'Cores',
            window: 'Sample window',
            window_value: '{seconds} s',
            of_total: '{used} of {total} used',

            // §8.6 — a marked partial report beats a failed one, but only if it says so.
            partial_title: 'These numbers describe the container, not the host',
            partial_hint:  'The agent was installed without the read-only host mount, so it can only measure itself. Everything else on this page is unaffected.',
            problems:      'What could not be read',

            none:      'No host metrics yet',
            none_hint: 'The agent reports host metrics every 60 seconds once it is running.',
        },

        containers: {
            title:  'Containers',
            name:   'Name',
            state:  'State',
            uptime: 'Uptime',
            image:  'Image',
            id:     'ID',

            managed:      'Managed',
            managed_hint: 'This panel owns this container and can restart or recreate it.',

            // §8.6 / D20 — a short list has two very different causes, and they look identical.
            filtered:  'This site reports only the containers this panel owns. The customer\'s other workloads are deliberately not sent.',
            truncated: 'Showing {shown} of {total} containers — the agent stops at its own cap so one runaway host cannot turn a report into a megabyte.',

            none:      'No containers reported',
            none_hint: 'The agent lists every container on the host once it is running.',
        },

        cert: {
            title:      'Certificates',
            expired:    'Expired',
            days_left:  '{days} d left',
            not_after:  'Expires {at}',
            total:      '{count} served',
            expiring:   '{count} expiring soon',
            critical:   '{count} expiring now',
            partial:    'A certificate source could not be read — an acme.json that is missing, unreadable, or being rewritten mid-renewal. What could be read is shown.',
            none:       'No certificates reported',
            none_hint:  'Traefik\'s built-in self-signed default is filtered out on purpose, so an instance serving only that shows nothing here.',
            tab_pending: 'The full inventory, manual uploads and delivery status are on the certificates tab.',
        },

        versions: {
            title:   'Versions',
            agent:   'Agent',
            traefik: 'Traefik',
            panel:   'Panel',
            started_at: 'running since {at}',
            tag_mismatch:      'Not the configured version',
            tag_mismatch_hint: 'This instance is configured for {expected}. Restarting or recreating the container is what makes the two agree.',
        },

        // §9.5's terminology policy: the three namespaces are Traefik nouns and stay in English.
        kinds: {
            routers:     'Routers',
            services:    'Services',
            middlewares: 'Middlewares',
        },

        table: {
            name:        'Name',
            status:      'Status',
            rule:        'Rule',
            service:     'Service',
            entrypoints: 'Entrypoints',
            type:        'Type',
            servers:     'Servers',
            used_by:     'Used by',
            owner:       'Group',
            provider:    'Provider',

            search_placeholder: 'Search by name, rule, service or group',
            count:      '{shown} of {total}',
            errors:     '{count} in error',
            error_title: 'Traefik reports on {name}',

            // W5.5 — an empty owner is a real answer, not a missing one.
            no_owner:      'Not from a group',
            no_owner_hint: 'No group on this instance claims this name. It comes from another Traefik provider — a Docker label on the host, or a file the panel does not manage.',

            no_match:      'Nothing matches the search',
            no_match_hint: 'Clear the search box to see everything Traefik reports.',
            none: {
                routers:     'Traefik reports no routers',
                services:    'Traefik reports no services',
                middlewares: 'Traefik reports no middlewares',
            },
            none_hint: 'Objects appear here as soon as Traefik loads a configuration that defines them.',

            truncated_title: 'This list is not the whole list',
            truncated_hint:  'The agent stopped paginating at its own cap. What is shown is a bounded sample, and the counts Traefik reports are the authority.',
        },
    },
    // The command queue (§4.3, §8.4, W6.9). The three command *types* are named in the operator's
    // words rather than in the wire's — `restart_traefik` is a value, "Restart Traefik" is a
    // sentence — but "Traefik" itself is a product name and stays as it is in both locales.
    command: {
        commands:  'Commands',
        command:   'Command',
        queue:     'Run command',
        in_flight: 'In progress',
        view:      'View command',
        open:      'Open',
        cancel:    'Cancel command',

        sequence:    '#',
        type_label:  'Command',
        status_label: 'Status',
        queued_at:   'Queued',
        finished_at: 'Finished',
        outcome:     'Outcome',
        attempts:    'Executed {count}×',
        duration:    'took {seconds} s at the site',
        deadline_at: 'Deadline {at}',
        payload:     'What it carries',
        raw_output:  'Output from the site',
        note:        'Nothing to do',

        // §9.5 — presentation only. The stored status stays `queued`; what changes is the sentence
        // an operator reads, because "queued" against a healthy site means "a few seconds from now"
        // and against a silent one means "go and look at the host".
        offline_title:      'This site is not reporting',
        offline_hint_seen:  'Queued commands are collected on the site\'s next sync. Its last report was {at}.',
        offline_hint_never: 'Queued commands are collected on the site\'s next sync, and this site has never reported.',

        rolled_back_short: 'Rolled back',
        rolled_back_title: 'The previous container was restored',
        rolled_back_hint:  'The recreate failed and the agent put the site back on the container it was running before. The site kept serving.',

        not_found:      'This command is not available',
        not_found_hint: 'It does not exist on this instance, or it has been pruned from the queue.',

        queued_toast:   'Command queued for delivery to the site.',
        queue_failed:   'The command could not be queued',
        cancel_confirm: 'Cancel the queued {type}? It stays in the queue as superseded, together with who cancelled it.',
        cancel_failed:  'The command could not be cancelled',

        // The lifecycle (§4.3). `delivering` and `delivered` are deliberately two words for two
        // facts: the panel put the command in a sync response, and the agent echoed the id back.
        status: {
            queued:         'Queued',
            delivering:     'Sending',
            delivered:      'Received by site',
            executing:      'Running',
            applied:        'Applied',
            failed:         'Failed',
            superseded:     'Cancelled',
            queued_offline: 'Queued — instance offline',
        },

        type: {
            restart_traefik:     'Restart Traefik',
            recreate_traefik:    'Recreate container',
            apply_static_config: 'Apply static config',
        },
        type_hint: {
            restart_traefik:     'Restarts the running Traefik container. The configuration files are not touched.',
            recreate_traefik:    'Replaces the container with one running the instance\'s image tag, keeping its name, labels and networks. Rolls back automatically if the new one does not come up healthy.',
            apply_static_config: 'Writes the panel\'s traefik.yml and traefik.env at the site, then restarts or recreates Traefik so they take effect. The previous files are backed up first.',
        },
        confirm: {
            restart_traefik:  'Restart Traefik at this site? Routing is interrupted for a few seconds while the container comes back up.',
            recreate_traefik: 'Recreate the Traefik container at this site? The agent pulls the image, replaces the container with one of the same name, labels and networks, and rolls back if the new one does not come up healthy.',
        },

        // The five timestamps and whose clock each one came from (§5.1, §8.6).
        ladder: {
            queued:     'Queued in the panel',
            delivering: 'Sent to the site',
            delivered:  'Received by the site',
            started:    'Execution started',
            finished:   'Execution finished',
        },
        clock: {
            panel: 'panel clock',
            site:  'site clock',
        },

        payload_image:                 'Image',
        payload_restart_mode:          'Restart mode',
        payload_restart_mode_hint:     'The panel\'s intent. The agent recreates instead of restarting whenever the environment file changed, because Docker fixes a container\'s environment when it is created.',
        payload_materials_hash:        'Materials hash',
        payload_materials_hash_hint:   'The files the site must already hold. The command carries no configuration itself — the bytes travelled separately and are verified against this digest.',
        payload_force_overwrite:       'Overwrite local changes',
        payload_force_overwrite_hint:  'This apply was authorised to overwrite a file that was changed at the site. The agent backs up the existing file before replacing it.',
        payload_none:                  'Nothing',
        payload_none_hint:             'A restart needs no parameters: everything it acts on is something the agent already knows about its own site.',

        restart_mode: {
            restart:  'restart',
            recreate: 'recreate',
        },

        // §8.4's failure taxonomy. Every value is a different next action, which is why the codes
        // exist instead of one red chip. An unrecognised code is shown verbatim (§8.6).
        error: {
            unsupported_command:  'The agent does not implement this command',
            invalid_payload:      'The agent rejected the command\'s parameters',
            precondition_failed:  'The site was not in the state this command requires',
            static_config_drift:  'The configuration file at the site was changed outside the panel',
            image_not_allowed:    'That image is outside the agent\'s allowlist',
            image_pull_failed:    'The image could not be pulled. Traefik kept running on the old one.',
            container_not_found:  'The agent could not find the Traefik container',
            health_gate_failed:   'The container did not become healthy in time',
            docker_error:         'Docker refused the operation',
            filesystem_error:     'The agent could not write at the site',
            timeout:              'No result arrived before the deadline',
            internal_error:       'The agent failed for an internal reason',
        },
    },
    // Certificate resolvers (§2.2, §7.11, W6.9). "Resolver", "challenge" and the lego provider ids
    // are Traefik/ACME domain vocabulary and stay in English in both locales (§9.5).
    resolver: {
        add:      'Add resolver',
        add_hint: 'Resolvers cannot be renamed later: the name keys the certificates already issued under it.',
        name:     'Name',
        name_hint: 'Letters, digits, dash and underscore; up to 32 characters. Rendered as certificatesResolvers.<name> in the static config.',
        name_invalid: 'Use only letters, digits, “-” and “_”, up to 32 characters.',
        challenge: 'Challenge',
        delete:    'Delete resolver',

        section_hint:  'The ACME certificate resolvers this instance declares. Adding, removing or re-credentialing one ships a new static configuration to the site.',
        none:          'This instance declares no resolvers.',
        default_badge: 'Default',
        not_removable: 'The default resolver is part of every instance and cannot be removed.',

        static_notice:             'Adding a resolver ships a new static configuration to the site.',
        credentials_static_notice: 'Replacing these credentials ships a new static configuration to the site and recreates the container, because Docker fixes a container\'s environment when it is created.',

        delete_confirm: 'Delete resolver “{name}”? It disappears from the site\'s traefik.yml on the next apply. Certificates already issued under it stay in the site\'s acme.json but stop renewing.',

        added:               'Resolver “{name}” added. A static configuration change was queued for the site.',
        deleted:             'Resolver “{name}” removed. A static configuration change was queued for the site.',
        credentials_replaced: 'Credentials for “{name}” replaced. A static configuration change was queued for the site.',
        credentials_missing:  'This dns01 resolver has no stored credentials.',

        replace_credentials:      'Replace credentials',
        replace_credentials_hint: 'For resolver “{name}”. The stored values are never shown — replacing them writes a complete new set.',

        challenge_hint: {
            tlsalpn01: 'Answered on port 443, which every instance already publishes. Needs nothing else.',
            http01:    'Answered on port 80, which must be reachable from the internet.',
            dns01:     'Answered by writing a DNS record. The only challenge that issues wildcards, and the only one that needs provider credentials.',
        },
    },
    // The DNS provider catalog (§7.11, `GET /dns-providers`). Provider names, ids and the
    // environment-variable names are served by the API and rendered verbatim — the operator matches
    // them against lego's own documentation.
    dns: {
        provider:      'DNS provider',
        provider_hint: 'The fields below are generated from the provider you choose, so you never type an environment-variable name.',
        select_provider: 'Select a provider',
        docs:          'Provider documentation',
        catalog_failed: 'The provider list could not be loaded. Reload the page to try again.',

        provider_id:             'lego provider id',
        provider_id_hint:        'The id exactly as lego spells it, for example “azuredns”. Nothing about it is checked by the panel.',
        provider_id_placeholder: 'provider-id',

        other_title: 'Uncurated provider',
        other_hint:  'Neither the id nor the variable names are validated. A mistake here produces a static configuration Traefik starts on and an ACME challenge that never completes.',

        variables:      'Environment variables',
        add_variable:   'Add variable',
        variable_name:  'VARIABLE_NAME',
        variable_value: 'Value',
        no_variables:   'No variables yet.',

        key_set:             'Credential type',
        key_set_primary:     'Recommended',
        key_set_alternative: 'Alternative',
        optional_variable:   'Optional',
        replace_whole_set:   'These credentials are replaced as a whole: fill in every field, including the ones you are not changing.',

        status: {
            recommended: 'Recommended',
            unavailable: 'Not available',
            unvalidated: 'Not validated',
        },
    },
    // The certificates tab (§4.5, §4.6, §7.2, §7.9, §9.1, W9.4/W9.7): served certificates — what
    // Traefik is actually presenting — and managed certificates — this panel's own uploads.
    certificate: {
        upload:      'Upload certificate',
        upload_hint: 'The key is never shown again after this dialog closes.',
        replace:      'Replace certificate',
        replace_hint: 'For “{name}”. Replacing a certificate changes its path, which is what makes Traefik reload it — no restart, no downtime.',
        delete:       'Delete certificate',

        name:            'Name',
        cert_pem:        'Certificate (PEM)',
        cert_pem_hint:   'The leaf certificate, optionally followed by its chain, exactly as issued.',
        cert_pem_format_hint: 'This does not look like a PEM certificate — it should start with “-----BEGIN CERTIFICATE-----”.',
        key_pem:         'Private key',
        key_pem_hint:    'Never shown back once stored (§4.5). Replacing it writes a complete new pair.',
        key_pem_format_hint: 'This does not look like a PEM private key — it should start with “-----BEGIN”.',

        no_restart_notice: 'Certificates are delivered to the site and picked up by a real configuration reload — never a restart, never downtime.',

        delete_confirm: 'Delete “{name}” ({domains})? Traefik stops serving it on the next reload.',
        deleted:        '“{name}” deleted.',
        saved_published: 'Certificate saved. Published as v{version}.',
        saved_unchanged: 'Certificate saved. It is identical to what was already published.',

        source: {
            acme:   'ACME',
            manual: 'Manual',
        },

        delivery: {
            staged:    'Staged',
            delivered: 'Delivered',
            serving:   'Serving',
        },

        served: {
            title: 'Served certificates',
        },

        managed: {
            title:         'Managed certificates',
            section_hint:  'Certificates added centrally in the panel and pushed to the site (§4.5). The private key is never shown again once stored.',
            none:          'No managed certificates yet.',
            none_hint:     'Upload a certificate to have this instance serve it.',
            none_read_only: 'No managed certificates, and you have read access only.',
        },
    },
    // §9.3's write-only field. It never displays a stored value and never asks for one: the panel
    // does not hold anything it could show (§5.1).
    write_only: {
        set_at:       'set {at}',
        set_by:       'set {at} by {by}',
        replace_hint: 'The stored value is replaced by whatever you type here.',
    },
    // The static-configuration tab (§6.1, §7.10, W6.8/W6.9/W6.11). "static config", the two file
    // names and "entrypoint" are Traefik's own vocabulary and stay in English in both locales
    // (§9.5): they are what an operator searches the Traefik documentation and the host for.
    static_config: {
        hint:         'Traefik reads these two files when it starts. The panel renders them from this instance\'s settings — there is no static-config editor in the panel: change the ACME address, the resolvers or the Traefik version, and the next apply writes the result at the site.',
        refresh:      'Reload',
        source_label: 'Source: {source}',
        rendered_at:  'Rendered {at}',
        // Keyed by the wire constant (`StaticConfigSource`), worded for the operator: `agent` is
        // declared and unused in v1, and the day a site reports its own bytes the label is here.
        source: {
            panel: 'Panel',
            agent: 'Site',
        },

        // §9.6 / R12 — the banner reports the server's flag and nothing else. The SPA never masks
        // a value, because masking in the browser would mean sending the plaintext to it first.
        redacted_title: 'Credential values are not shown',
        redacted:       'This instance stores DNS credentials. The server removes their values before this page is built, so the file at the site holds more than this screen shows.',

        // Three states, not two: "matches" is claimed only when both digests exist and agree.
        in_sync:       'Matches the site',
        not_compared:  'Not compared yet',

        // §7.10 rule 3 — a drifted site reports its traefik.yml, so there usually *is* a
        // before/after. These are the three cases where there is not, and each one sends the
        // operator somewhere different: wait for a report, look at traefik.env, or look at the file
        // itself. An empty diff would read as "these files are identical", which is the one thing
        // that is certainly untrue while the digests differ.
        diff_unavailable: {
            on_disk_body_not_reported: 'The site has not reported the contents of its file — an older agent, or a site that has not applied a configuration yet. The digests above are what the panel can compare.',
            on_disk_body_matches:      'The traefik.yml at the site is the one the panel shipped, so the difference is in traefik.env — the file that holds the DNS credentials, which never leaves the host — or in a value that is hidden on both sides.',
            diff_too_large:            'The file at the site is too large to compare line by line. The digests above still show that it differs.',
            unknown:                   'The panel cannot show a before/after for this difference.',
        },

        drift: {
            title:       'The file at the site was changed',
            badge:       'Changed at the site',
            alert_title: 'Static config changes are blocked',
            alert:       'Someone edited traefik.yml or traefik.env on the host. Until that is resolved, every change that would rewrite them is refused rather than applied — the panel does not silently destroy a hand edit. Fold the change into this instance\'s settings, or discard the host\'s copy.',

            panel_digest: 'Last shipped by the panel',
            host_digest:  'Reported by the site',
            digest_none:  'Nothing reported',
            host:         'At the site',
            panel:        'From the panel',

            diff_unavailable_title: 'No before/after to show',
            discard_action:         'Discard host changes',
            read_only:              'Resolving this needs write access to the instance.',
        },

        discard: {
            title:    'Discard host changes',
            subtitle: 'The files at the site are replaced with the panel\'s on “{name}”.',

            consequence_title: 'This cannot be undone from the panel',
            consequence:       'Whatever was changed on the host stops taking effect the moment the new files are applied. The panel never read those files and cannot restore them for you.',

            point_backup:      'The agent copies the existing files into data/agent/backup/<timestamp>/ at the site before replacing them.',
            point_entrypoints: 'Entrypoints are fixed at install, so an entrypoint added by hand disappears. A change worth keeping has to be folded into the instance\'s settings instead.',
            point_restart:     'Traefik is restarted — or recreated, if the environment file changed — so the new configuration takes effect. Routing is interrupted while it comes back up.',

            type_to_confirm: 'Type “{name}” to confirm',
            confirm:         'Discard and overwrite',
            queued:          'The overwrite was queued for the site. The host\'s files are backed up before they are replaced.',
            failed:          'The overwrite could not be queued',
        },
    },
    // The group screens (§9.4, W4.5). "Group", like router/service/middleware, is a Traefik-domain
    // noun and stays in English in both locales (§9.5) — as do the names of the six definition
    // namespaces, which are rendered verbatim from the API.
    group: {
        groups: 'Groups',
        group:  'Group',
        new:    'New group',
        add:    'Add group',

        name: 'Name',
        // D25 — the display name is the panel's, not Traefik's. The key below is the Traefik one.
        name_hint:    'Shown in the panel only. It never reaches the Traefik configuration.',
        name_invalid: 'Use at most 64 characters.',

        key:             'Key',
        key_hint:        'Names both the router and the service this group defines.',
        key_hint_linked: 'Names both the router and the service this group defines. Prefilled from the domain until you edit it.',
        // `{'@'}` is a vue-i18n **literal**, not a placeholder: a bare `@` starts a linked message
        // (`@:key`) and a message carrying one is a compile error, which blanks the whole subtree
        // it renders in rather than only the sentence. `yarn check:i18n` compiles every message
        // for exactly this reason — see `scripts/check-i18n-messages.mjs`.
        key_invalid:     'Start with a letter or digit, then letters, digits, “.”, “-” or “_”, up to 63 characters. “{\'@\'}” is not allowed — Traefik reads it as a provider suffix.',
        key_reserved:    'Names beginning with “system-” are reserved for the panel\'s own groups.',

        domain:         'Domain',
        domain_hint:    'Exactly one hostname — the router matches Host(`…`) on it. A wildcard or a second domain needs the advanced type.',
        domain_invalid: 'Enter one hostname, for example app.example.com. An internationalised domain has to be entered in its xn-- (punycode) form.',

        destination_url:     'Destination URL',
        destination_hint:    'The single server this group forwards to, for example http://10.0.0.10:8080. Traefik forwards the request\'s own query string, so this takes none.',
        destination_invalid: 'Start with http:// or https:// and include a host, with no query string, fragment or credentials.',

        insecure_skip_verify: 'Destination certificate',
        insecure_switch:      'Skip TLS verification for the destination',
        // §7.8 point 5, D30 — the toggle references the instance-wide system transport rather than
        // creating a per-group one, which is why it is a switch and not a form.
        insecure_hint:    'Offered for https:// destinations only. It points the service at the instance\'s shared system transport.',
        insecure_cleared: 'The destination no longer uses https://, so skipping TLS verification was switched off. Set it again if you go back to an https:// destination.',

        // §7.4 / D31 — the key is one field naming two definitions, so editing it is a paired
        // rename. Saving routes it through the rename cascade dialog (W8.9).
        paired_rename_title: 'Renaming “{from}” to “{to}” moves two definitions',
        paired_rename_hint:  'The key names the router and the service together, so both are renamed at once. Saving opens the rename dialog first, which shows every group the rename touches before anything is written.',

        type:  'Type',
        state: 'State',
        types: {
            simple:   'Simple',
            advanced: 'Advanced',
            manual:   'Manual',
            system:   'System',
        },
        type_unknown_hint: 'This build does not recognise this group\'s type. The generated YAML and the references tab still show the current state of the group.',

        // W8.11's seatbelt (`isRenderableStoredSource`): the stored source document is not the
        // shape this editor writes, so binding a form to it would show an empty editor over a real
        // configuration — and the editor's save replaces the whole document.
        source_unreadable:      'This group cannot be edited here',
        source_unreadable_hint: 'Its stored source document is not in the shape this editor writes, so no form is shown — an empty form here would overwrite the real configuration on the first save. The generated YAML, references and history tabs show what the group actually contains. Report this: it means the document was written by something other than this editor.',

        enabled:  'Enabled',
        disabled: 'Disabled',

        system:       'System',
        system_title: 'This group belongs to the panel',
        // §7.8 — a system group is a real row, and §2.4 shows it on purpose: its names are
        // reserved and take part in collision checking, so an operator has to be able to see it.
        system_hint: 'The panel compiles it from the instance\'s settings and rewrites it whenever those change, so it cannot be edited here. Its names are reserved and take part in collision checks.',
        system_kinds: {
            redirect:          'the system HTTP → HTTPS redirect',
            shared_transports: 'the system shared transport',
            certificates:      'the system certificates group',
            unknown:           'a group the panel owns',
        },

        validation: {
            ok:       'Valid',
            invalid:  'Invalid',
            warnings: 'Warnings',
            unknown:  'Not validated',
        },
        validation_label:         'Validation',
        validation_ok_hint:       'This group passes every check.',
        validation_errors_hint:   'Validation errors: {count}',
        validation_warnings_hint: 'Warnings: {count}',

        defines:          'Defines',
        definitions_more: '+{count} more',
        version:          'Version',
        version_label:    'Version {version}',
        unsaved:          'Unsaved changes',

        search_placeholder: 'Search by name or definition',
        search_none:        'No group matches the search.',
        count:              '{shown} of {total} shown',
        none:               'No groups yet',
        none_hint:          'Add a group to route a domain to a destination. The site picks the change up within seconds.',
        none_read_only:     'This instance has no groups, and you have read access only.',

        tabs: {
            editor:     'Editor',
            yaml:       'YAML',
            references: 'References',
            history:    'History',
        },

        mode_title: 'What kind of group is this?',
        // D36 — conversions are one-way, so the picker says so before the form is filled in.
        mode_hint:  'Chosen once: a group can later be converted simple → advanced → manual, never back.',
        modes: {
            simple:        'Simple',
            simple_hint:   'One domain, one destination.',
            advanced:      'Advanced',
            // D61/O-20 — the picker states the narrowing up front rather than letting an operator
            // discover it halfway through a form.
            advanced_hint: 'Routers, services and middlewares with typed forms and a rule builder. Anything outside this matrix requires manual mode.',
            manual:        'Manual',
            manual_hint:   'YAML you write yourself, for anything the forms do not cover.',
        },

        // The advanced editor (§9.4, W8.2/W8.2b): `AdvancedGroupForm`, `RouterCard`, `ServiceCard`,
        // `MiddlewareCard` and `RuleNode`. `name_invalid`/`name_duplicate` are shared by router,
        // service and middleware names — all three namespaces enforce the same alphabet
        // (`TraefikConfig.validDefinitionName`).
        advanced: {
            routers_title:     'Routers',
            add_router:        'Add router',
            routers_none:      'No routers yet',
            routers_none_hint: 'Add a router to match traffic and send it to a service.',

            services_title:     'Services',
            add_service:        'Add service',
            services_none:      'No services yet',
            services_none_hint: 'Add a service so a router has something to forward to.',

            // `{'@'}` — a vue-i18n literal, see `group.key_invalid`'s own comment above.
            name_invalid:   'Start with a letter or digit, then letters, digits, “.”, “-” or “_”, up to 63 characters. “{\'@\'}” is not allowed — Traefik reads it as a provider suffix.',
            name_duplicate: 'Another router, service or middleware in this group already uses this name.',

            middlewares_title:     'Middlewares',
            add_middleware:        'Add middleware',
            middlewares_none:      'No middlewares yet',
            middlewares_none_hint: 'Add a middleware and attach it to a router, or chain it from another middleware.',

            router: {
                name:      'Name',
                name_hint: 'Names this router — a Traefik name, not a display name.',
                remove:    'Remove router',
                unnamed:   'Unnamed router',

                service:             'Service',
                service_hint:        'The service this router forwards matching traffic to.',
                service_placeholder: 'Pick or type a service name',

                middlewares:              'Middlewares',
                middlewares_hint:         'Applied to every request this router matches, in the order listed.',
                middlewares_placeholder:  'Pick or type a middleware name',

                entry_points:      'Entry points',
                entry_points_hint: 'Leave empty to use every entrypoint. Type a name and press enter to add it.',

                priority:      'Priority',
                priority_hint: 'Higher wins when two routers could both match. Leave empty for Traefik’s rule-length default.',

                tls:                'Serve over TLS',
                tls_hint:           'Turns this into an HTTPS router.',
                tls_options:        'TLS options',
                tls_cert_resolver:  'Certificate resolver',
            },

            rule: {
                title: 'Rule',
                hint:  'What this router matches. Groups are always shown in parentheses, so AND/OR is never ambiguous.',

                depth_indicator: '{depth} of {max}',
                depth_hint:      'This rule is nested as deep as the advanced editor allows — flatten it, or switch to manual mode for a rule this shaped.',

                matcher:          'Matcher',
                arg:              'Argument',
                arg_n:            'Argument {n}',
                add_arg:          'Add argument',
                remove_arg:       'Remove argument',
                all_of:           'All of',
                any_of:           'Any of',
                negate:           'Not',
                add_condition:    'Add condition',
                remove_condition: 'Remove condition',

                kinds: {
                    matcher: 'Matcher',
                    and:     'AND',
                    or:      'OR',
                    not:     'NOT',
                },

                arg_errors: {
                    backtick: 'Cannot contain a backtick (`) — Traefik v3 rule syntax has no way to escape one.',
                    hostname: 'Enter a hostname, for example app.example.com.',
                    path:     'Start with “/”.',
                    regex:    'Enter a valid regular expression.',
                    free:     'This field is required.',
                    method:   'Pick one of the HTTP methods Traefik’s Method matcher recognises.',
                    cidr:     'Enter an IP address or a CIDR block.',
                },
            },

            service: {
                name:    'Name',
                remove:  'Remove service',
                unnamed: 'Unnamed service',

                kind:      'Type',
                kind_hint: 'How this service reaches a backend.',
                kinds: {
                    loadBalancer: 'Load balancer',
                    weighted:     'Weighted',
                    mirroring:    'Mirroring',
                    failover:     'Failover',
                },

                servers:            'Servers',
                server_placeholder: 'http://10.0.0.10:8080',
                add_server:         'Add server',
                pass_host_header:   'Pass Host header',

                servers_transport:      'Servers transport',
                servers_transport_hint: 'Leave empty for Traefik’s default transport.',

                sticky:           'Sticky sessions',
                sticky_hint:      'Pins a client to the same server with a cookie.',
                sticky_name:      'Cookie name',
                sticky_secure:    'Secure',
                sticky_http_only: 'HTTP only',
                sticky_same_site: 'SameSite',
                same_site_none:    'None',
                same_site_lax:     'Lax',
                same_site_strict:  'Strict',
                same_site_invalid: 'Pick “None”, “Lax” or “Strict”.',

                health_check:              'Health check',
                health_check_hint:         'An active check Traefik uses to take an unhealthy server out of rotation.',
                health_check_path:         'Path',
                health_check_path_invalid: 'Start with “/”.',
                health_check_interval:     'Interval',
                health_check_timeout:      'Timeout',
                duration_invalid:          'Enter a duration Traefik can parse, for example “10s” or “500ms”.',

                weighted_targets: 'Weighted targets',
                add_target:       'Add target',
                weight:           'Weight',

                main_service:      'Main service',
                main_service_hint: 'Every request goes here; the mirrors below only receive a copy.',
                mirrors:           'Mirrors',
                add_mirror:        'Add mirror',
                percent:           'Percent',

                primary:       'Primary service',
                fallback:      'Fallback service',
                failover_same: 'The fallback cannot be the same service as the primary.',
            },

            // MiddlewareCard (W8.2b, D61): the thirteen typed forms plus the generic key/value
            // editor. `regex_invalid` is shared by every regex-bearing field (redirectRegex,
            // stripPrefixRegex, replacePathRegex) rather than repeated per form.
            middleware: {
                name:    'Name',
                remove:  'Remove middleware',
                unnamed: 'Unnamed middleware',

                kind:      'Type',
                kind_hint: 'Which Traefik middleware this is.',
                kinds: {
                    redirectScheme:   'Redirect scheme',
                    redirectRegex:    'Redirect (regex)',
                    stripPrefix:      'Strip prefix',
                    stripPrefixRegex: 'Strip prefix (regex)',
                    addPrefix:        'Add prefix',
                    replacePath:      'Replace path',
                    replacePathRegex: 'Replace path (regex)',
                    headers:          'Headers',
                    basicAuth:        'Basic auth',
                    ipAllowList:      'IP allow list',
                    rateLimit:        'Rate limit',
                    chain:            'Chain',
                    errors:           'Error pages',
                    generic:          'Other (generic)',
                },

                regex_invalid: 'Enter a valid regular expression, at most 256 characters.',

                redirect_scheme: {
                    scheme:       'Scheme',
                    port:         'Port',
                    port_hint:    'Leave empty to keep the request’s own port.',
                    port_invalid: 'A port is digits only.',
                    permanent:    'Permanent (301, instead of 302)',
                },

                redirect_regex: {
                    regex:            'Regex',
                    replacement:      'Replacement',
                    replacement_hint: 'Traefik’s own regexp replacement syntax, for example $1 or ${1}.',
                    permanent:        'Permanent (301, instead of 302)',
                },

                strip_prefix: {
                    prefixes:         'Prefixes',
                    add_prefix:       'Add prefix',
                    force_slash:      'Force a leading slash',
                    force_slash_hint: 'Adds “/” back if stripping the prefix would leave the path without one.',
                },

                strip_prefix_regex: {
                    patterns:    'Patterns',
                    add_pattern: 'Add pattern',
                },

                add_prefix: {
                    prefix: 'Prefix',
                },

                replace_path: {
                    path: 'Path',
                },

                replace_path_regex: {
                    regex:       'Regex',
                    replacement: 'Replacement',
                },

                headers: {
                    custom_request_headers:  'Custom request headers',
                    custom_response_headers: 'Custom response headers',
                    add_header:              'Add header',
                    header_name:             'Name',
                    header_value:            'Value',

                    cors_title:                        'CORS',
                    access_control_allow_credentials:  'Allow credentials',
                    access_control_allow_headers:      'Allowed headers',
                    access_control_allow_methods:      'Allowed methods',
                    access_control_allow_origin_list:  'Allowed origins',
                    access_control_expose_headers:     'Exposed headers',
                    access_control_max_age:            'Max age (seconds)',
                    add_vary_header:                   'Add a Vary header',

                    security_title:              'Security headers',
                    sts_seconds:                 'HSTS max age (seconds)',
                    sts_include_subdomains:      'HSTS applies to subdomains',
                    sts_preload:                 'HSTS preload',
                    force_sts_header:            'Send HSTS over plain HTTP too',
                    frame_deny:                  'Deny framing (X-Frame-Options: DENY)',
                    custom_frame_options_value:  'Custom X-Frame-Options value',
                    content_type_nosniff:        'X-Content-Type-Options: nosniff',
                    browser_xss_filter:          'X-XSS-Protection',
                    content_security_policy:     'Content-Security-Policy',
                    referrer_policy:             'Referrer-Policy',
                    permissions_policy:          'Permissions-Policy',
                },

                basic_auth: {
                    users:             'Users',
                    users_hint:        'htpasswd format: user:hashed-password.',
                    add_user:          'Add user',
                    user_invalid:      'Not “user:hashed-password” (htpasswd format).',
                    realm:             'Realm',
                    remove_header:     'Remove the Authorization header before forwarding',
                    header_field:      'Forward the username in this header',
                    header_field_hint: 'Leave empty to not forward the username at all.',
                },

                ip_strategy: {
                    title:         'Custom IP strategy',
                    hint:          'How the client IP is determined from the request. Leave off for Traefik’s own default.',
                    depth:         'Depth',
                    depth_invalid: 'A depth is not negative.',
                    excluded_ips:  'Excluded addresses',
                },

                ip_allow_list: {
                    source_range:               'Allowed addresses',
                    add_address:                'Add address',
                    reject_status_code:         'Rejection status code',
                    reject_status_code_invalid: 'An HTTP status code is between 100 and 599.',
                },

                rate_limit: {
                    average:                'Average (requests per period)',
                    average_invalid:        'Average is greater than zero.',
                    period:                 'Period',
                    burst:                  'Burst',
                    burst_invalid:          'A burst is not negative.',
                    source_criterion_title: 'Source criterion',
                    request_header_name:    'Request header name',
                    request_host:           'Use the request host instead of the client IP',
                },

                chain: {
                    middlewares:             'Middlewares',
                    middlewares_hint:        'Applied in the order listed, on top of whatever else the request already passed through.',
                    middlewares_placeholder: 'Pick or type a middleware name',
                },

                errors: {
                    status:                'Status codes',
                    status_hint:           'A three-digit HTTP status, or a “NNN-NNN” range.',
                    add_status:            'Add status',
                    status_invalid:        'Enter a three-digit HTTP status or a “NNN-NNN” range.',
                    service:               'Error page service',
                    service_hint:          'Serves the custom error page for the status codes above.',
                    query:                 'Query',
                    query_hint:            'A path template for the error page, for example /{status}.html. Leave empty for Traefik’s default.',
                    status_rewrites:       'Status rewrites',
                    add_rewrite:           'Add rewrite',
                    rewrite_from:          'From',
                    rewrite_from_invalid:  'A rewritten status is a three-digit HTTP status.',
                    rewrite_to:            'To',
                    rewrite_to_invalid:    'A rewritten status is between 100 and 599.',
                },

                generic: {
                    type:            'Traefik middleware type',
                    type_hint:       'The field name this middleware compiles under, for example compress or inFlightReq.',
                    type_typed:      '“{type}” has a typed form above — pick it from the type picker instead of using this one twice.',
                    type_whitespace: 'Has leading or trailing whitespace.',

                    config:      'Configuration',
                    config_hint: 'A key/value tree, validated when you save — the same rules Traefik itself applies to this middleware type.',
                    add_key:     'Add key',
                    key_placeholder: 'Key',

                    value_type: {
                        string:  'Text',
                        number:  'Number',
                        boolean: 'Yes / No',
                        null:    'Empty',
                        array:   'List',
                        object:  'Map',
                    },
                    array_item:    'Item {n}',
                    add_item:      'Add item',
                    duplicate_key: 'Another key at this level already uses this name.',
                },
            },
        },

        // The manual editor (§9.4, W8.2): `AppYamlEditor` is the editor, with no separate
        // generated-YAML tab (`visibleGroupEditorTabs`) — this document is what is stored.
        manual: {
            yaml:      'YAML',
            yaml_hint: 'The document exactly as you write it. Saving preserves it byte-for-byte apart from the tokens a rename cascade actually renames (D35).',

            syntax_error_title: 'This is not valid YAML yet',
            syntax_error_hint:  'Line {line}, column {col}. Fix the syntax before saving.',

            advanced_options: 'Advanced options',

            allow_unknown_fields:      'Allow unknown fields',
            allow_unknown_fields_hint: 'Lets this document use a Traefik field newer than this panel knows about. Off by default (§7.6).',

            acknowledge_global_tls:      'Allow claiming the default TLS options or store',
            acknowledge_global_tls_hint: 'Defining tls.options/default or tls.stores/default reconfigures TLS for the whole instance. Turn this on only when that is deliberate.',
        },

        // §7.1, §9.4 — the YAML is rendered from the compiled fragment on demand and never stored.
        yaml_hint:      'Rendered by the server from the compiled group. Read-only: the group is stored as a structured document, and this is what it produces.',
        yaml_none:      'Nothing to render yet',
        yaml_none_hint: 'The draft has not compiled. Fix the problems on the editor tab and it appears here.',

        preview_pending:     'Checking…',
        preview_unavailable: 'Live validation is unavailable right now. What is shown may be out of date — a save is checked by the server either way.',

        // §7.3 step 5's delta scoping, in three headings: what this save introduces, what the
        // instance already carried, and what is advisory.
        problems_blocking:     'This group cannot be saved yet',
        problems_pre_existing: 'Already present on this instance',
        problems_warnings:     'Worth reading',
        problem_in_group:      'in group “{group}”',

        collision: {
            title:      'That name is already taken',
            // §9.4 — the system case names the panel feature that holds the name, because the
            // operator has never opened the group that owns it and could not find it.
            system:     '“{name}” is reserved by {feature}.',
            group_name: 'A group named “{name}” already exists on this instance.',
            definition: '“{name}” is already defined in {kind} by the group “{group}”.',
            open_owner: 'Open “{group}”',
        },

        definitions_title:      'Definitions this group claims',
        definitions_hint:       'Names it owns on this instance. Another group claiming one of them is rejected, and this group cannot be deleted while something still references it.',
        definitions_none:       'No definitions',
        definitions_none_hint:  'This group compiles to a document that defines nothing.',
        references_title:       'References this group makes',
        // `{'@'}internal` — a vue-i18n literal, see `group.key_invalid`'s own comment above. This
        // is the message whose compile error blanked the whole References tab.
        references_hint:        'Names it points at. Each one has to resolve to a definition of an enabled group, a Traefik built-in or an allowlisted {\'@\'}internal target, or the save is refused.',
        references_none:        'No references',
        references_none_hint:   'Nothing in this group points at a name defined elsewhere.',
        reference_site:         'Site',
        reference_site_hint:    'The reference site (R1 … R14) — which field of the configuration holds this name.',
        // §7.3 — R13 and R14 point into `instances.resolvers`, so no group defines their target.
        reference_resolver_hint: 'A certResolver is defined in the instance\'s static configuration, not by a group.',
        reference_owner:        'Held by',
        target_namespace:       'Target',
        target_name:            'Name',
        namespace:              'Namespace',
        definition_name:        'Name',
        pointer:                'Path',

        // The inbound half of the references tab (§9.1, W8.9): which groups name *this* group's
        // definitions. It reuses §7.5's own delete-preflight listing rather than a second endpoint
        // computing the identical answer (`GroupReferencesTab`'s own file comment).
        inbound_title:          'Referenced by',
        inbound_hint:           'Other groups that point at a name this group defines. Deleting or disabling this group is blocked while any of them do.',
        inbound_unsaved_title:  'Nothing to show yet',
        inbound_unsaved_hint:   'This group has not been saved, so it defines nothing another group could reference.',
        inbound_unavailable:    'This listing could not be read right now. Reopen this tab to try again.',
        inbound_none:           'Nothing references this group',
        inbound_none_hint:      'No other group points at a name this one defines. It can be deleted or disabled without breaking anything else.',
        inbound_group:          'Group',
        inbound_target:         'Its name',

        history_title:   'History',
        history_unsaved: 'This group has not been saved yet, so it has no history.',

        // §7.7's history tab (W8.10): every version, a diff between any two of them, and revert.
        history: {
            none: 'No versions yet',

            version_column: 'Version',
            kind_column:    'Change',
            summary_column: 'What happened',

            reverted_from: 'Reverted from v{version}',

            revert:         'Revert to this version',
            revert_title:   'Revert group',
            // §7.7 — a revert replays the target's source through the normal save pipeline, so it
            // re-validates and can be refused exactly like any other save; the copy says so rather
            // than promising a rewind that always succeeds.
            revert_confirm: 'Revert to version {version}? This replays that version\'s configuration through a normal save. If a name it used has been taken since, the revert is refused — nothing is overwritten.',

            compare_from: 'Compare from',
            compare_to:   'Compare to',

            // `GroupChangeKind` (`pkg/enums/GroupChangeKind`, §7.7) — why one version exists.
            kinds: {
                create:    'Created',
                save:      'Saved',
                rename:    'Renamed',
                enable:    'Enabled',
                disable:   'Disabled',
                delete:    'Deleted',
                restore:   'Restored',
                revert:    'Reverted',
                rollback:  'Rolled back',
                system:    'System recompile',
                converted: 'Converted',
            },
        },

        // D36's one-way type conversion (§6.1, §9.4, W8.11) — never a lossy downgrade, and never
        // offered as a reverse.
        convert: {
            action:  'Convert to {to}',
            button:  'Convert',
            title:   'Convert group',
            // The one sentence that matters: there is no way back (D36).
            confirm: 'Convert this group from {from} to {to}? This cannot be undone — a {to} group cannot be converted back to {from}. Nothing about the configuration itself changes; only the editor you author it through does.',
            success: 'Converted to {to}.',
        },

        // §7.4/§9.4's rename cascade — `GroupRenameCascadeDialog` (W8.9): plan, review, confirm,
        // apply. `kind` in every key below is `renameKindLabel`'s own plain word (router, service,
        // middleware, …), not a Traefik namespace string — those stay technical everywhere else in
        // this screen, but a sentence reads better with the word.
        rename: {
            title: 'Rename {kind} “{from}” to “{to}”',
            intro: 'Review every place this rename would change. Nothing is written until you confirm.',

            // §9.4's offer, shown on the editor page when the only thing blocking the save is that
            // the renamed definition is still referenced elsewhere — the case the cascade exists
            // for, and the one where the Save button that used to open it is disabled.
            offer_title:  'This save is blocked because other groups still reference the old name',
            offer_hint:   'Renaming a definition here does not update the groups pointing at it, so the save is refused. The rename cascade does both at once: it renames the definition and rewrites every reference to it, in one reviewed step.',
            offer_action: 'Rename with cascade',

            loading_plan: 'Building the rename plan…',
            plan_failed:  'Could not build a rename plan. Close this dialog and try again.',

            // §7.4 step 5 — the plan changed between opening this dialog and confirming it.
            stale_notice: 'This plan changed since you opened it — an edit landed on one of the affected groups. Review the refreshed plan below and confirm again.',

            // D31's own case: a simple group's key names its router and its service together.
            paired_title: 'This also renames a paired definition',
            paired_line:  '{kind} “{from}” → “{to}”',

            blocking_title: 'This rename cannot be applied yet',

            owner_badge:      'Owner',
            formatting_badge: 'Reformatted',

            column_path:       'Where',
            column_before:     'Before',
            column_after:      'After',
            definition_move:   'Definition name',

            show_diff: 'Show document diff',
            hide_diff: 'Hide document diff',
            diff_hint: 'A preview: every occurrence of the renamed name, highlighted in the document. The applied change touches only the sites listed above — never text inside a comment.',

            formatting_group_note: 'This group\'s YAML could not be patched token-for-token and will be re-encoded instead — comments and key order are kept, blank lines are not.',

            // §7.4/D35's consent gate, read before the confirm button is offered at all.
            formatting_title:    'These groups will be reformatted',
            formatting_intro:    'Their YAML could not be patched token-for-token, so it is re-encoded instead. Comments and key order are kept; blank lines are not.',
            formatting_consent:  'I understand these groups will be reformatted',

            // §9.4's own words: the confirm button states the blast radius.
            confirm_button:         'Rename and update {count} reference(s) in {groups} group(s)',
            confirm_button_pending: 'Confirm',

            success_published: 'Renamed. Published as snapshot v{version}; the site picks it up within seconds.',
            success_unchanged: 'Renamed. Nothing changed, so no new snapshot was published.',
        },

        // §7.7 step 3 — an identical save publishes nothing, and that is a success, not a failure.
        saved_published: 'Saved. Published as snapshot v{version}; the site picks it up within seconds.',
        saved_unchanged: 'Saved. Nothing changed, so no new snapshot was published.',

        not_available:      'Group not available',
        not_available_hint: 'It does not exist, or you do not have access to it.',

        // §2.3's toggle and §3.3's delete (§7.5, W4.6). Two sentences carry the whole model, and
        // both are things operators get wrong: a disabled group serves nothing and **keeps every
        // name it claims** (D26), and a deleted one releases them for anybody to take (D37).
        enable:           'Enable',
        disable:          'Disable',
        enable_confirm:   'Enable “{name}”? Its routes reach the site within about 15 seconds.',
        disable_confirm:  'Disable “{name}”? It stops serving immediately. Its names stay reserved, so you can switch it back on at any time.',
        enabled_success:  'Group enabled.',
        disabled_success: 'Group disabled. It keeps its names reserved.',

        delete:         'Delete group',
        delete_confirm: 'Delete “{name}”? The {count} name(s) it holds are released, and another group can claim them.',
        delete_success: 'Group deleted.',

        // §7.5 / §9.4 — a rejection, not a confirmation: the copy explains what to fix and offers
        // no way to proceed, because there is none.
        blocked: {
            delete_title:  'This group cannot be deleted yet',
            disable_title: 'This group cannot be disabled yet',
            delete_intro:  'Deleting “{name}” would remove definitions that other configuration still points at. Traefik answers a reference it cannot resolve with a 404 rather than refusing to load, so those routes would exist and stop working.',
            disable_intro: 'Disabling “{name}” takes its definitions out of the published configuration, and other configuration still points at them. Traefik answers a reference it cannot resolve with a 404 rather than refusing to load, so those routes would exist and stop working.',
            summary:       'References that would be left pointing at nothing: {sites}, in {groups} group(s).',
            // §7.5's own edge case: chaining `system-redirect-to-https` is legitimate, and the
            // group holding the name is one the operator cannot edit.
            system_owner: 'This is one of the panel\'s own groups ({feature}), so it cannot be edited here — remove the reference from your own group instead.',
            open_group:   'Open “{group}”',
            kept:         'Nothing is released by disabling: a disabled group keeps every name it claims.',
            next:         'Remove or repoint these references, then try again.',
        },
    },
    // The publish history (§2.5, §5, §7.7, §9.1, W9.1): every configuration Traefik has been
    // asked to serve, a detail view for one of them, and a diff between any two. "Snapshot" is
    // Traefik-adjacent domain vocabulary and stays in English in both locales (§9.5); `cause`
    // reuses `group.history.kinds` via `getGroupChangeKind` rather than a second dictionary of the
    // same `GroupChangeKind` events — a snapshot and the group version that produced it describe
    // one change in one vocabulary.
    snapshot: {
        history_title: 'Publish history',
        history_hint:  'Every configuration Traefik has been asked to serve, in the order it was published.',

        version_column:     'Version',
        cause_column:       'Cause',
        composition_column: 'Composition',
        published_column:   'Published',

        view: 'View snapshot',
        open: 'Open',

        // §9.5's own rule for AppDiffViewer's change badge, applied here too: a bare number behind
        // a tooltip rather than an interpolated "{n} groups" — the app has no plural rules
        // configured, and Slovene needs four forms where English needs two.
        members_tooltip: 'Groups included in this snapshot',
        members_none:    'Nothing — routes nothing',
        by_group:        'from “{name}”',

        not_found:      'This snapshot is not available',
        not_found_hint: 'It does not exist on this instance, or the panel could not read it right now.',

        detail: {
            title: 'Snapshot v{version}',

            content_hash:      'Content hash',
            size_label:        'Size',
            composition_title: 'Composition',
            // §7.7 — this is the whole answer to "what would rolling back to this version restore".
            composition_hint:  'The exact group versions this snapshot was built from — what a rollback to this version would restore.',
            yaml_title:        'Rendered configuration',
        },

        // §7.7/ADR-0008's rollback (W9.2/W9.3) — `SnapshotRollbackDialog`: plan, review, confirm,
        // apply. The composition diff renders as three sections, `remove` loudest of the three —
        // it is the only one of the three that is not reversible from the dialog itself.
        rollback: {
            action: 'Roll back to this version',
            title:  'Roll back to snapshot v{version}',
            intro:  'Review exactly what this would change before anything is written. Nothing is written until you confirm.',

            loading_plan: 'Building the rollback plan…',
            plan_failed:  'Could not build a rollback plan. Close this dialog and try again.',

            stale_notice: 'This plan changed since you opened it — something moved on one of the affected groups. Review the refreshed plan below and confirm again.',

            no_op: 'This instance is already at snapshot v{version}. There is nothing to roll back.',

            revert_title:  'Groups reverted to an earlier version',
            restore_title: 'Groups restored',
            restore_deleted:  '(deleted)',
            restore_disabled: '(disabled)',

            // The loudest section — §7.7's own words for `remove[]`. A group here keeps no trace
            // of ever having existed after this rollback except its own version history.
            remove_title:    'Groups that will be removed',
            remove_warning:  'These groups were created after snapshot v{version} and are not part of its configuration. Rolling back soft-deletes them — they stop serving traffic immediately. Their history is kept, so they can be restored again later by rolling back to a version that included them.',
            remove_consent:  'I understand these {count} group(s) will be removed',

            blocked_title: 'This rollback cannot be applied yet',
            blocked_line:  '“{key}” is claimed by “{taken_by}” now — it belonged to “{group}” in v{version}',

            blocking_title: 'This rollback would introduce problems',

            // A warning, never a reason to disable Confirm — see IRollbackUnavailableCertificate's
            // own comment for why: the composition still replays exactly, but a certificate that
            // was replaced or deleted since v{version} left no copy of its old bytes anywhere in
            // the system, so this one entry will stay undeliverable after the rollback.
            unavailable_certificates_title: 'A certificate from this version can no longer be delivered',
            unavailable_certificates_line:  '“{path}” — this certificate was replaced or deleted since v{version}; its old content no longer exists anywhere and cannot be served again',
            unavailable_certificates_hint:  'The rollback itself will still succeed, but Traefik will keep serving its own default certificate for this entry instead — indefinitely, unless you upload a matching certificate again after confirming.',

            resulting_yaml_title: 'Resulting configuration',
            resulting_yaml_hint:  'What this instance would serve after the rollback — identical to snapshot v{version}\'s own document.',

            confirm_button:         'Roll back {count} group(s)',
            confirm_button_pending: 'Confirm',
            confirm_button_none:    'Nothing to roll back',

            success_published: 'Rolled back to v{version}. Published as snapshot v{new_version}; the site picks it up within seconds.',
            success_unchanged: 'Already at v{version}. Nothing changed, so no new snapshot was published.',
        },

        // §9.1's promise: a diff between *any* two published versions, not only adjacent ones — an
        // operator investigating when a domain changed reaches for exactly this.
        diff: {
            title:        'Compare versions',
            hint:         'Pick any two published versions. Both documents are rendered and compared in your browser.',
            compare_from: 'Compare from',
            compare_to:   'Compare to',
            need_two:     'Publish a second version to compare it against this one.',
            load_failed:  'Could not load one of these versions.',
        },
    },
    // The in-stack registry page (§16.1 D74, W16.x): what the panel's own registry serves to the
    // agents, and the mirror daemon that fills it. "Registry", "tag", "digest" and "agent" are
    // registry-domain vocabulary and stay in English in both locales (§9.5), like
    // router/service/middleware — they are what an operator types into a `docker pull`.
    registry: {
        title:       'Agent images',
        description: 'What this panel\'s own registry serves to the agents. The mirror below copies images into it from the source registry; agents pull from here anonymously.',

        // The RegistryUnavailable sentinel's dedicated empty-state — the page keeps polling, so
        // this heals itself the moment the registry container answers again.
        unavailable:      'The registry did not answer',
        unavailable_hint: 'The panel could not query its registry over the internal network. This page retries automatically.',

        empty:      'No images yet',
        empty_hint: 'Images appear here once the mirror copies the first tag from the source registry, or once one is pushed from the panel host.',

        collected_at: 'collected {at}',

        copy_digest:   'Copy digest',
        digest_copied: 'Digest copied',
        copy_failed:   'The browser did not allow the copy. Use the tooltip and copy the digest manually.',

        mirror: {
            title: 'Mirror',

            status: {
                unconfigured: 'Not configured',
                idle:         'Idle',
                syncing:      'Syncing',
                error:        'Error',
            },

            // §13.1 — the three keys are deploy-time configuration, set where the panel is
            // deployed and deliberately not settable from here (the fleet_paused_hint honesty).
            unconfigured_hint: 'Mirroring is not configured for this deployment. Set REGISTRY_MIRROR_SOURCE, REGISTRY_MIRROR_USERNAME and REGISTRY_MIRROR_TOKEN where the panel is deployed to have images copied here automatically.',

            source:       'Source repository',
            destination:  'Destination repository',
            interval:     'Sync interval',
            last_sync:    'Last sync',
            last_success: 'Last success',
            last_error:   'Last error',

            missing_refs:      'Missing references',
            missing_refs_hint: 'References the fleet is instructed to run that do not exist in the source registry yet — a visible miss, not an error. The mirror checks again on every pass.',

            tags_synced: 'Tags in sync',
            tags_copied: 'Tags copied',
        },

        table: {
            repository: 'Repository',
            tag:        'Tag',
            digest:     'Digest',
            platforms:  'Platforms',
            size:       'Size',
            created:    'Created',
        },
    },
    // §8.3/§8.4's shared vocabulary — access levels, scope kinds and source kinds — consumed by
    // `libs/user.ts` and `AccessSourceBadges` from both transparency views (M10, W10.5).
    access: {
        level: {
            label: 'Access level',
            none:  'No access',
            read:  'Read',
            write: 'Write',
        },
        scope: {
            label:    'Scope',
            client:   'Client',
            instance: 'Instance',
        },
        source: {
            label:              'Sources',
            role_tooltip:       'Granted by the “{role}” role.',
            client_badge:       'Client: {name}',
            client_tooltip:     'Assigned on client “{name}” ({level}). Covers every instance of this client, including ones added later.',
            instance_badge:     'Direct assignment',
            instance_tooltip:   'Assigned directly on “{name}” ({level}).',
        },
        no_sources: 'No access',
        // §10.4: an admin or an `all_instances:*` role reaches the whole fleet, so the forward
        // view shows the one to three roles that explain it instead of listing every instance.
        covers_all_instances: 'This grants access to every instance in the fleet, including ones added later.',
    },
    // /users, /users/:userId and /profile (§8, §9.1, W10.3/W10.5). Role and level vocabulary
    // lives in `access` above; this is the users-screen-specific copy.
    users: {
        user:   'User',
        name:   'Name',
        status: 'Status',
        active:   'Active',
        disabled: 'Disabled',
        you:      'You',
        enable:   'Enable',
        disable:  'Disable',
        not_available: 'This user is not available. It does not exist, or you do not have access to it.',
        last_activity: 'Last activity',
        // Bumped by both a sign-in and an administrator's own edit to this account — see
        // `Users.vue`'s own comment on why this is not labelled "last sign-in".
        self_notice: 'This is your own account. Nobody may edit their own roles, assignments or status here — ask another administrator.',
        enable_confirm:  'Enable “{name}”? They will be able to sign in again immediately.',
        disable_confirm: 'Disable “{name}”? They will be signed out on their next request and unable to sign in again until re-enabled.',
        danger_zone: 'Danger zone',
        delete:      'Delete user',
        delete_hint: 'Only offered for a user who has never held a role or an assignment — deleting anyone else would lose their audit trail.',
        delete_confirm: 'Delete “{name}”? This cannot be undone.',
        roles: {
            title: 'Roles',
            none:  'No roles',
            admin_locked_hint: 'Only an administrator may grant or revoke the administrator role.',
            last_admin_hint:   'This is the last active administrator. Grant the administrator role to someone else before removing it here.',
            admin: {
                label:       'Administrator',
                description: 'Full permissions, including managing users and granting the administrator role itself.',
            },
            user_manager: {
                label:       'User manager',
                description: 'Can manage users — roles and assignments — but grants no access to any instance.',
            },
            all_instances_read: {
                label:       'All instances (read)',
                description: 'Can view every Traefik instance in the fleet. Cannot change anything.',
            },
            all_instances_write: {
                label:       'All instances (write)',
                description: 'Can view and change every Traefik instance in the fleet.',
            },
        },
        assignments: {
            title: 'Assignments',
            none:  'No scoped assignments',
            add:   'Add assignment',
            remove: 'Remove assignment',
            remove_confirm: 'Remove this assignment on “{name}”?',
            granted_on: 'Granted {date}',
            pick_client:   'Search clients…',
            pick_instance: 'Search instances…',
            client_hint:   'Covers every instance of this client, including ones added later.',
            instance_hint: 'Covers this one instance only.',
            pick_from_list:     'Pick from the list instead',
            enter_id_manually:  'Enter an ID manually instead',
        },
        access: {
            title: 'Access',
        },
    },
    // The two App* editor wrappers (§9.3). "YAML" is a Traefik-adjacent domain noun and stays in
    // English in both locales, like router/service/middleware (§9.5).
    editor: {
        loading:        'Loading editor…',
        load_failed:    'The editor could not be loaded. Reload the page to try again.',
        yaml:           'YAML',
        read_only:      'Read-only',
        view_split:     'Split',
        view_unified:   'Unified',
        before:         'Before',
        after:          'After',
        no_changes:     'No changes',
        changed_blocks: 'Changed blocks',
        // W12.5's size guard. The hint says what the wait buys and what it costs, because the
        // operator is the one deciding: this is the only place the document can be read.
        large_diff_title:  'This comparison is large',
        large_diff_hint:   'Comparing documents this size takes a moment and freezes the page while it runs.',
        large_diff_render: 'Compare anyway',
        large_diff_size:   'Combined size of both documents',
    },
    errors: {
        generic_error: 'An error occurred',
        invalid_data:  'Invalid data',
        not_found:     'Not found',
        unauthorized:  'Unauthorized',
        forbidden:     'You do not have permission to do that',
        on_add:        'Error while adding',
        on_edit:       'Error while updating',
        on_delete:     'Error while deleting',
        on_load:       'Error while loading data',
        // The §6.1 sentinels the panel can already produce.
        name_collision:              'That name is already taken',
        still_referenced:            'This is still in use and cannot be deleted',
        unsupported_traefik_version: 'That Traefik version is outside the supported range',
        // The configuration engine's refusals. The details are never in the envelope (§6.1) — the
        // screen re-reads its preflight resource and shows the listing that explains them.
        reserved_name:           'That name is reserved by the panel',
        reference_dangling:      'This would leave a reference pointing at nothing',
        invalid_traefik_config:  'Traefik would not accept this configuration',
        unsupported_yaml_feature: 'That YAML feature is not supported',
        unsupported_section:     'That configuration section cannot be written by a group',
        config_too_large:        'That configuration is too large',
        // §7.4 step 5 — `GroupRenameCascadeDialog` handles this one itself by re-rendering the
        // refreshed plan the envelope carries; this is the fallback for a caller that does not.
        rename_plan_stale:       'That rename plan is no longer current',
        // §7.7/ADR-0008 — `SnapshotRollbackDialog` handles this one itself, the same way the rename
        // dialog handles its own stale-plan 409: the listing lives on the plan's `blocked[]`, this
        // is only the fallback for a caller that skipped it.
        snapshot_composition_conflict: 'That rollback can no longer be applied — a name it needs has been taken since',
        // M6's three (§6.1). The drift one is a gate rather than a complaint: the panel refuses to
        // overwrite a file somebody changed at the site, and the static-config tab is where the
        // diff and the audited discard are (§7.10 rule 3).
        unknown_dns_provider:    'That DNS provider or credential set is not one the panel can use',
        static_config_drift:     'The configuration file at the site was changed outside the panel, so it was not overwritten',
        command_not_cancellable: 'That command has already reached the site and can no longer be cancelled',
        // §4.5, W9.4 — the certificate and the private key were not both usable, or did not match.
        invalid_certificate: 'The certificate and key could not be used together — check that the private key matches the certificate',
        // W10.3's guardrails (§10.4, D44). `last_admin_removal` is also rendered inline, with its
        // own explanation, next to the control that triggered it (`RolesEditor`, the disable
        // button) — this generic sentence is the fallback for a toast that has no such control.
        self_modification_forbidden: 'You cannot make this change to your own account — ask another administrator',
        last_admin_removal:          'This would leave the panel with no administrator',
        user_has_access_history:     'This user has held a role, an assignment, or an audit entry, and cannot be deleted — disable them instead',
        // D74's 502 — the panel is up, its registry container is not answering. `Registry.vue`
        // renders this as its own empty-state; this sentence is the fallback for any other caller.
        registry_unavailable:        'The panel\'s registry did not answer — try again in a moment',
        site_not_host_networked:     'This site was installed before the stack moved to host networking, so it cannot use the configuration this panel renders — re-run its install command on the host with TPM_FORCE=1. Nothing was changed',
        // D76's session-plane sentinels. The CSRF refusal is a 403 the panel itself never causes
        // (a same-origin fetch carries the evidence by itself) — the fix is upstream, so the advice
        // is a reload and never a re-login. The store one is a 503: nothing was granted or revoked.
        cross_origin_request:        'This request was rejected as cross-origin. Reload the panel and try again',
        session_store_unavailable:   'The session store is unavailable. Try again shortly',
    },
    success: {
        add:    'Successfully added',
        edit:   'Successfully updated',
        delete: 'Successfully deleted',
    },
    validation: {
        required:  'This field is required',
        email:     'Invalid email address',
        object_id: 'Pick an item from the list, or enter its 24-character ID',
    },
    table: {
        rows_per_page: 'Rows per page',
        filter:        'Filter',
        clear_filters: 'Clear filters',
    },
}
